<template>
  <ul
    :id="resolvedListId"
    ref="listRef"
    :class="rootClasses"
    role="listbox"
    data-cy="ds-option-list"
    :tabindex="props.disabled ? undefined : props.keyboardNavigationEnabled ? 0 : -1"
    :aria-activedescendant="activeOptionId"
    @focus.capture="onFocus"
    @blur.capture="onBlur"
    @keydown="onListKeydown"
  >
    <template
      v-for="(item, index) in props.options"
      :key="index"
    >
      <DSOptionListGroupItem
        v-if="isOptionGroup(item)"
        v-model="modelValue"
        :option="item"
        :multiple="props.multiple"
        :disabled="props.disabled"
        :list-id="resolvedListId"
        :active-option-id="activeOptionId"
      >
        <template
          v-if="hasSuffixSlot"
          #suffix="slotProps"
        >
          <slot
            name="suffix"
            v-bind="slotProps"
          />
        </template>
      </DSOptionListGroupItem>

      <DSOptionListItem
        v-else
        v-model="modelValue"
        :option="item"
        :multiple="props.multiple"
        :disabled="props.disabled"
        :option-id="getOptionId(resolvedListId, item)"
        :active="isOptionActive(item)"
      >
        <template
          v-if="hasSuffixSlot"
          #suffix="slotProps"
        >
          <slot
            name="suffix"
            v-bind="slotProps"
          />
        </template>
      </DSOptionListItem>
    </template>

    <template v-if="!props.options.length">
      <DSOptionListItem
        v-if="props.loading"
        disabled
        :option="{ label: LOADING_MESSAGE, value: null }"
      />
      <DSOptionListItem
        v-else
        disabled
        :option="{ label: EMPTY_MESSAGE, icon: 'error', value: null }"
      />
    </template>
  </ul>
</template>

<script setup lang="ts">
import { computed, ref, toRef, useSlots } from "vue";
import type { DSOptionListEmits } from "../types/emits";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { crossesFocusBoundary } from "@/src/design_system/form_elements/shared/utils/focusBoundary";
import { DS_OPTION_LIST_CLASSES } from "@/src/design_system/form_elements/option_list/constants/classes";
import DSOptionListGroupItem from "@/src/design_system/form_elements/option_list/components/DSOptionListGroupItem.vue";
import DSOptionListItem from "@/src/design_system/form_elements/option_list/components/DSOptionListItem.vue";
import { DSOptionListModelValue, DSOptionListProps } from "../types";
import { useStateClasses } from "@/src/design_system/shared/composables/useStateClasses";
import { getOptionId, isOptionGroup } from "@/src/design_system/form_elements/option_list/utils/optionListUtils";
import { useOptionListIdentity } from "@/src/design_system/form_elements/option_list/composables/useOptionListIdentity";
import { useOptionListActiveOption } from "@/src/design_system/form_elements/option_list/composables/useOptionListActiveOption";
import { useOptionListKeyboardNavigation } from "@/src/design_system/form_elements/option_list/composables/useOptionListKeyboardNavigation";
import { useOptionListSelection } from "@/src/design_system/form_elements/option_list/composables/useOptionListSelection";

const props = withDefaults(defineProps<DSOptionListProps>(), {
  disabled: false,
  multiple: false,
  loading: false,
  listId: undefined,
  keyboardNavigationEnabled: true,
});

const modelValue = defineModel<DSOptionListModelValue>({ default: "" });
const emit = defineEmits<DSOptionListEmits>();
const listRef = ref<HTMLUListElement>();
const { focus } = useDSInput(computed(() => (props.disabled ? null : listRef.value)));
const blur = () => {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && listRef.value?.contains(activeElement)) activeElement.blur();
};
const onFocus = (event: Event) => {
  if (!crossesFocusBoundary(event, listRef.value)) return;
  emit("focus", event);
};
const onBlur = (event: Event) => {
  if (!crossesFocusBoundary(event, listRef.value)) return;
  emit("blur", event);
};

defineExpose({ focus, blur });
const activeOptionId = defineModel<string | undefined>("active", { default: undefined });

const EMPTY_MESSAGE = window.I18n.designSystem.optionList.emptyMessage;
const LOADING_MESSAGE = window.I18n.designSystem.optionList.loadingMessage;

const { stateClasses } = useStateClasses(props);

const slots = useSlots();
const hasSuffixSlot = computed(() => Boolean(slots.suffix));

const rootClasses = computed(() => ({
  [DS_OPTION_LIST_CLASSES.base]: true,
  ...stateClasses.value,
}));

const { resolvedListId } = useOptionListIdentity(toRef(props, "listId"));

const { flatOptions, activeIndex, isOptionActive } = useOptionListActiveOption(
  toRef(props, "options"),
  activeOptionId,
  resolvedListId
);

const { selectOption } = useOptionListSelection({
  modelValue,
  multiple: toRef(props, "multiple"),
  disabled: toRef(props, "disabled"),
});

const { onListKeydown } = useOptionListKeyboardNavigation({
  listRef,
  keyboardNavigationEnabled: toRef(props, "keyboardNavigationEnabled"),
  activeOptionId,
  activeIndex,
  flatOptions,
  resolvedListId,
  selectOption,
});
</script>
